import { DOMNode, domToReact, Element } from 'html-react-parser';

import { sanitizeHtml } from 'utils/sanitizeHtml';

import {
  TestFunction,
  TestFunctionsConfig,
} from '../HTMLShadowDomRenderer.types';
import { EmailReplyQuote } from '../components/emailReplyQuote/emailReplyQuote';

/**
 * Contains the configuration for test functions used in replace function. Refer
 * to usage examples in the type definition and add new test function in the
 * proper section if you want to support new quote style.
 */
const testFunctionsConfig: TestFunctionsConfig = {
  children: [
    (domNode) => isTagName(domNode, 'blockquote'),
    (domNode) => hasClass(domNode, 'dixa_quote'),
    (domNode) => isTagWithClass(domNode, 'div', 'yahoo_quoted'),
    (domNode) => isTagWithClass(domNode, 'div', 'gmail_quote'),
  ],
  nextSiblings: [
    (domNode) => hasClass(domNode, 'gmail_quote_attribution'),
    (domNode) =>
      isTagName(domNode, 'hr') && hasStyle(domNode, [outlookHrStyle]),
    (domNode) => hasClass(domNode, 'moz-cite-prefix'),
    (domNode) => hasStyle(domNode, outlookQuotesStyles),
    (domNode) =>
      hasSomeChildPassingTest(
        domNode,
        (childNode) =>
          hasStyle(childNode, outlookQuotesStyles) &&
          childNode.parent?.children.length === 1,
      ),
  ],
  allSiblings: [
    (domNode) => isTagWithClass(domNode, 'br', 'Apple-interchange-newline'),
    (domNode) =>
      isTagName(domNode, 'spark_quote') || hasClass(domNode, 'spark_quote'),
  ],
};

/**
 * Replaces a node with quote if certain conditions are met.
 *
 * @param domNode The node to be processed
 */
export function replaceReplyQuote(domNode: DOMNode): JSX.Element | DOMNode {
  if (!isElement(domNode)) {
    return domNode;
  }

  /**
   * If a node has a previous sibling passing "next siblings" test functions, it
   * means that it has already been moved to the quote section. So, it should be
   * removed it in order to prevent duplicate renders.
   */
  if (
    testFunctionsConfig.nextSiblings.some((test) =>
      hasSomePreviousSiblingPassingTest(domNode, test),
    )
  ) {
    return <></>;
  }

  /**
   * If a node passes "next siblings" test functions, it should be moved with
   * its all next siblings into quote section.
   */
  if (testFunctionsConfig.nextSiblings.some((test) => test(domNode))) {
    return (
      <EmailReplyQuote>{domToReact(getNextSiblings(domNode))}</EmailReplyQuote>
    );
  }

  /**
   * If a node passes "children" test functions, its children should be moved to
   * quote section.
   *
   * Also, if a node has some child passing "all siblings" test function, its
   * children should be moved to quote section.
   */
  if (
    testFunctionsConfig.children.some((test) => test(domNode)) ||
    testFunctionsConfig.allSiblings.some((test) =>
      hasSomeChildPassingTest(domNode, test),
    )
  ) {
    return <EmailReplyQuote>{domToReact(domNode.children)}</EmailReplyQuote>;
  }

  return domNode;
}

/* Checks if a node is of type Element */
function isElement(domNode: DOMNode): domNode is Element {
  return 'attribs' in domNode;
}

/** Checks if a node contains one of the given styles. */
function hasStyle(
  domNode: DOMNode,
  styles: readonly string[],
): domNode is Element {
  return (
    isElement(domNode) &&
    styles.some((style) => domNode.attribs.style?.includes(style))
  );
}

/** Checks a node contains the given class. */
function hasClass(domNode: DOMNode, className: string): domNode is Element {
  return (
    isElement(domNode) && domNode.attribs.class?.split(' ').includes(className)
  );
}

/** Checks if a node has the given name. */
function isTagName(domNode: DOMNode, tagName: string): domNode is Element {
  return isElement(domNode) && domNode.name === tagName;
}

/** Checks if a node has the give name and contains the given class. */
function isTagWithClass(
  domNode: DOMNode,
  tagName: string,
  className: string,
): domNode is Element {
  return isTagName(domNode, tagName) && hasClass(domNode, className);
}

/** Checks if a node contains a child passing test function. */
function hasSomeChildPassingTest(
  domNode: DOMNode,
  test: TestFunction,
): domNode is Element {
  return isElement(domNode) && domNode.children.some(test);
}

/** Checks if a node contains a previous sibling passing given test function */
function hasSomePreviousSiblingPassingTest(
  domNode: DOMNode,
  test: TestFunction,
): domNode is Element {
  for (
    let previousSibling = domNode.previousSibling;
    previousSibling;
    previousSibling = previousSibling.previousSibling
  ) {
    if (test(previousSibling)) {
      return true;
    }
  }

  return false;
}

/** Returns next siblings (inclusive) of a given node */
function getNextSiblings(domNode: DOMNode): DOMNode[] {
  const nextSiblings: DOMNode[] = [domNode];
  for (
    let nextSibling = domNode.nextSibling;
    nextSibling;
    nextSibling = nextSibling?.nextSibling
  ) {
    nextSiblings.push(nextSibling);
  }

  return nextSiblings;
}

/**
 * Sanitize styles.
 *
 * Since the content of the message is sanitized, the style attributes might
 * differ, e.g. removed white space and/or dangling semicolons. Therefore, the
 * style attributes that are used for querying the the shadow dom should be
 * sanitized as well.
 */
function getSanitizedStyle(style: string): string {
  return sanitizeHtml(`<span style="${style}"></span>`, 'email')
    .split('style="')[1]
    .split('">')[0];
}

/** Outlook quote styles */
const outlookQuotesStyles = [
  'border:none;border-top:solid #E1E1E1 1.0pt;padding:3.0pt 0', // Outlook 2013  (american) <color=#E1E1E1> <unit=pt>
  'border:none;border-top:solid #E1E1E1 1.0cm;padding:3.0cm 0', // Outlook 2013 (international) <color=#E1E1E1> <unit=cm>
  'border:none;border-top:solid #B5C4DF 1.0pt;padding:3.0pt 0', // Outlook 2007, 2010 (american) <color=#B5C4DF> <unit=pt>
  'border:none;border-top:solid #B5C4DF 1.0cm;padding:3.0cm 0', // Outlook 2007, 2010 (international) <color=#B5C4DF> <unit=cm>
  'padding-top: 5px; border-top-color: rgb(229, 229, 229); border-top-width: 1px; border-top-style: solid;', // Windows Mailer
].map(getSanitizedStyle);

/** Outlook <hr /> elem style */
const outlookHrStyle = getSanitizedStyle('display:inline-block; width:98%');
